/**
 * Custom hook for managing provisioning state
 */
import { useState, useCallback } from 'react';
import {
  ProvisioningComponent,
  CreatedAppData,
  SignalType,
  isAppCreationSignal,
  isGitHubRepoSignal,
  isCnapTenancySignal,
  isAdGroupSignal,
} from '../types';
import {
  ApplicationResponse,
  GithubRepo,
  cnapResponse,
  AdGroupResponse,
} from '@devxdiscover/devhub-common';
import { initialProvisioningComponents } from '../constants';

/**
 * Hook for managing provisioning state and progress
 * @returns Provisioning state and update functions
 */
export const useProvisioningState = () => {
  const [provisioningComponents, setProvisioningComponents] = useState<
    ProvisioningComponent[]
  >(initialProvisioningComponents);
  const [overallProgress, setOverallProgress] = useState(0);
  const [isFailed, setIsFailed] = useState(false);
  const [provisioningComplete, setProvisioningComplete] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [createdAppData, setCreatedAppData] = useState<CreatedAppData | null>(
    null,
  );
  const [adGroups, setAdGroups] = useState<
    Array<{ groupName: string; groupId: string }>
  >([]);
  const [githubRepos, setGithubRepos] = useState<GithubRepo[]>([]);
  const [_cnapTenancy, setCnapTenancy] = useState<cnapResponse | null>(null);

  /**
   * Calculates overall progress based on discrete checkpoints
   * Uses a 6-state model:
   * - State 1 (0%): All pending (initial state)
   * - State 2 (10%): Provisioning accepted by API, processing started
   * - State 3 (25%): Application created
   * - State 4 (50%): GitHub repository created
   * - State 5 (75%): AD Groups created
   * - State 6 (100%): CNAP tenancy created
   * Progress only advances on completion, never decreases
   * @param components - Array of provisioning components
   * @returns Overall progress percentage (0-100)
   */
  const calculateOverallProgress = (
    components: ProvisioningComponent[],
  ): number => {
    // Find components by their event type IDs
    const appCreation = components.find(c => c.id === 'app-creation');
    const githubRepo = components.find(c => c.id === 'github-repo');
    const adGroupsComponent = components.find(c => c.id === 'ad-groups');
    const cnapTenancy = components.find(c => c.id === 'cnap-tenancy');

    // Calculate discrete progress based on completed or skipped checkpoints
    let progress = 0;

    // If any component has started (not pending), provisioning has been accepted
    const hasStarted = components.some(
      c => c.status !== 'pending' && c.status !== 'failed',
    );
    if (hasStarted) {
      progress = 10;
    }

    if (
      appCreation?.status === 'completed' ||
      appCreation?.status === 'skipped'
    ) {
      progress = 25;
    }
    if (
      githubRepo?.status === 'completed' ||
      githubRepo?.status === 'skipped'
    ) {
      progress = 50;
    }
    if (
      adGroupsComponent?.status === 'completed' ||
      adGroupsComponent?.status === 'skipped'
    ) {
      progress = 75;
    }
    if (
      cnapTenancy?.status === 'completed' ||
      cnapTenancy?.status === 'skipped'
    ) {
      progress = 100;
    }

    return progress;
  };

  /**
   * Updates completed states based on signal data
   * @param task - The signal task with completion data
   */
  const setCompletedStates = (
    task: SignalType<
      ApplicationResponse | GithubRepo | cnapResponse | AdGroupResponse
    >,
  ) => {
    if (isAppCreationSignal(task)) {
      setCreatedAppData({
        applicationId: task.data.applicationId,
        name: task.data.applicationName,
        description: task.data.applicationDescription,
        ownerLogin: task.data.ownerLogin,
      });
    } else if (isGitHubRepoSignal(task)) {
      setGithubRepos(prev => [...prev, task.data]);
    } else if (isCnapTenancySignal(task)) {
      setCnapTenancy(task.data);
    } else if (isAdGroupSignal(task)) {
      setAdGroups(task.data?.adGroups ?? []);
    }
  };

  /**
   * Updates a specific provisioning component's state
   * @param event - The event/component id to update
   * @param status - The new status
   * @param progress - The progress percentage (0-100)
   * @param signal - Optional signal data for completed state
   */
  const updateProvisioningComponent = useCallback(
    (
      event: string,
      status: ProvisioningComponent['status'],
      progress: number,
      signal?: SignalType<
        ApplicationResponse | GithubRepo | cnapResponse | AdGroupResponse
      >,
    ) => {
      setProvisioningComponents(prev => {
        const updated = prev.map(component =>
          component.id === event
            ? {
                ...component,
                status,
                // Never decrease progress - keep the higher value
                progress:
                  status === 'completed' || status === 'skipped'
                    ? 100
                    : Math.max(component.progress, progress),
                // Capture error message from failed signals
                error: status === 'failed' ? signal?.error : component.error,
              }
            : component,
        );
        setOverallProgress(calculateOverallProgress(updated));
        setIsFailed(updated.some(c => c.status === 'failed'));

        // Check if all components are completed or skipped
        const allCompleted = updated.every(
          c => c.status === 'completed' || c.status === 'skipped',
        );
        if (allCompleted) {
          setProvisioningComplete(true);
        }

        if ((status === 'completed' || status === 'skipped') && signal) {
          setCompletedStates(signal);
        }

        return updated;
      });
    },
    [], // Empty deps - all functions used inside are from useState setters which are stable
  );

  /**
   * Resets all provisioning state to initial values
   */
  const resetProvisioning = () => {
    setProvisioningComponents(initialProvisioningComponents);
    setOverallProgress(0);
    setIsFailed(false);
    setProvisioningComplete(false);
    setMutationError(null);
    setIsRetrying(false);
    setCreatedAppData(null);
    setAdGroups([]);
    setGithubRepos([]);
    setCnapTenancy(null);
  };

  /**
   * Smart reset for retry - preserves completed components and their data
   * Only resets failed/pending components to allow seamless retry
   */
  const resetProvisioningForRetry = () => {
    setProvisioningComponents(prev => {
      const updated = prev.map(component => {
        // Preserve completed and skipped components as-is
        if (
          component.status === 'completed' ||
          component.status === 'skipped'
        ) {
          return component;
        }
        // Reset failed and pending components
        const initialComponent = initialProvisioningComponents.find(
          c => c.id === component.id,
        );
        return initialComponent || component;
      });

      // Recalculate progress to maintain checkpoint of last completed resource
      const newProgress = calculateOverallProgress(updated);
      setOverallProgress(newProgress);

      return updated;
    });

    setIsFailed(false);
    setProvisioningComplete(false);
    setMutationError(null);
    // Don't reset isRetrying - let caller manage this flag
    // Keep createdAppData, adGroups, githubRepos, and cnapTenancy - don't reset completed data
  };

  return {
    provisioningComponents,
    overallProgress,
    isFailed,
    provisioningComplete,
    setProvisioningComplete,
    mutationError,
    setMutationError,
    isRetrying,
    setIsRetrying,
    createdAppData,
    adGroups,
    githubRepos,
    updateProvisioningComponent,
    resetProvisioning,
    resetProvisioningForRetry,
  };
};
